import React, { FC, useState, useCallback, useEffect, useRef } from 'react'
import ModalWrapper from '../../components/Modal/ModalWrapper'
import Input from '../../components/Input'
import Button from '../../components/Button'
import CrossIcon from '../../icons/cross.svg'

interface IThresholdModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (threshold: number | null) => void
  currentThreshold?: number | null
  globalThreshold?: number | null
}

const ThresholdModal: FC<IThresholdModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentThreshold,
  globalThreshold,
}) => {
  const [thresholdValue, setThresholdValue] = useState<string>(
    currentThreshold?.toString() ?? '',
  )
  const [error, setError] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setThresholdValue(currentThreshold?.toString() ?? '')
    setError('')
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [currentThreshold, isOpen])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSave()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        handleCancel()
      }
    },
    [thresholdValue, setThresholdValue],
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isOpen, handleKeyDown])

  const handleSave = useCallback(() => {
    if (inputRef.current) {
      setThresholdValue(inputRef.current.value)
    }

    const trimmed = thresholdValue.trim()

    if (trimmed === '') {
      onSave(null)
      onClose()
      return
    }

    const parsed = parseInt(trimmed, 10)

    // Validate that trimmed contains only digits (no minus, no other chars)
    if (isNaN(parsed) || !/^-?\d+$/.test(trimmed)) {
      setError('Please enter an integer')
      return
    }

    if (parsed < 0) {
      setError('Must be 0 or more')
      return
    }

    onSave(parsed)
    onClose()
  }, [thresholdValue, onSave, onClose])

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  if (!isOpen) return null

  return (
    <ModalWrapper className="import">
      <button className="import_backdrop clear-button" onClick={onClose} />
      <div className="import_dialog action-modal threshold-modal-dialog">
        <div className="action-modal_title">Minimum threshold</div>
        <CrossIcon className="product-modal_close" onClick={onClose} />
        <div className="action-modal_info">Per location</div>
        <div className="action-modal_field">
          <Input
            ref={inputRef}
            type="string"
            value={thresholdValue}
            onChange={(e) => {
              setThresholdValue(e.target.value)
              setError('')
            }}
            // error={error}
          />
          {error && <div className="threshold-modal_error">{error}</div>}
          {/*<div className="threshold-modal_hint">Empty the value to remove threshold</div>*/}
          {globalThreshold && (
            <div className="threshold-modal_hint">
              Global setting: {globalThreshold}
            </div>
          )}
        </div>
        <Button block color="primary" onClick={handleSave}>
          Save
        </Button>
        <Button
          block
          color="primary-link"
          className="action-modal_cancel"
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </div>
    </ModalWrapper>
  )
}

export default ThresholdModal
